/**
 * Normalizes a stored phone value to E.164 (e.g. `+15551234567`) for display and validation.
 *
 * inspector-admin stored phones as `(123) 456-7890` — 10 digits, implicitly US — and the field was
 * optional (see the removed `PhoneValidation.REGEX`), so legacy rows may hold that format, a bare
 * digit string, or an empty value. This form now requires E.164, and react-phone-input-2 misreads the
 * legacy separators (`(123) 456-7890` renders as `+1 (234) 567-890`), so we convert on form open.
 *
 * Empty/blank values are left empty — normalization can't invent a number, and the required-field
 * validation surfaces the missing phone.
 */
export const normalizePhone = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  if (!trimmed) return '';

  // Already E.164-ish: preserve the leading `+`, drop any display separators after it.
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  }

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';

  // 10 digits → legacy US number (area code + subscriber), add the +1 country code.
  if (digits.length === 10) return `+1${digits}`;
  // 11 digits starting with 1 → US number missing only the leading `+`.
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;

  // Anything else: prefix `+` and let validation decide whether it's acceptable.
  return `+${digits}`;
};
